import { money, multiplyByRatio, type Money } from "@stb/core/money"

/**
 * Individual income tax rules and a pure take-home pay engine (T913, spec
 * docs/specs/pay-calculator-spec.md). Thresholds are whole dollars, rates are basis points
 * (15% = 1500), results are integer cents. Estimate only, not tax advice.
 */

export interface Source {
  label: string
  /** ATO page the value was read from. */
  url: string
  /** Date (`YYYY-MM-DD`) the page was opened and the value checked. */
  checkedOn: string
}

/**
 * `published`: on the ATO page for that year. `provisional`: the ATO has not published the year,
 * the previous year's value is used. `announced`: the ATO page says it applies, but the legislation
 * text has not been verified.
 */
export type RuleStatus = "published" | "provisional" | "announced"

/** Income over `from` dollars is taxed at `rateBp`, on top of `baseDollars`. */
export interface Bracket {
  from: number
  baseDollars: number
  rateBp: number
}

export interface StudyLoanBand extends Bracket {
  /** The rate applies to the whole repayment income, not only the part over `from`. */
  ofWholeIncome: boolean
}

export interface IncomeTaxYear {
  financialYear: string
  resident: { brackets: readonly Bracket[]; status: RuleStatus; source: Source }
  foreignResident: { brackets: readonly Bracket[]; status: RuleStatus; source: Source }
  workingHoliday: {
    brackets: readonly Bracket[]
    status: RuleStatus
    source: Source
    /** The ATO page read does not say; treated as not liable for the levy and no LITO. */
    medicare: "none" | "resident-rules"
    lito: boolean
    treatmentStatus: RuleStatus
  }
  lito: {
    fullDollars: number
    firstTaperFrom: number
    firstTaperBp: number
    secondFrom: number
    secondBaseDollars: number
    secondTaperBp: number
    cutOut: number
    source: Source
  }
  medicare: {
    rateBp: number
    singleLower: number
    singleUpper: number
    shadeInBp: number
    status: RuleStatus
    source: Source
  }
  studyLoan: { bands: readonly StudyLoanBand[]; source: Source }
  superGuarantee: { rateBp: number; maxContributionBaseAnnual: number; source: Source }
  /** General concessional (before-tax) super contributions cap, whole dollars a year (T916). */
  concessionalCap: { dollars: number; source: Source }
  standardDeduction: {
    maxDollars: number
    residentsOnly: true
    status: RuleStatus
    source: Source
  } | null
}

// T1011: all ATO pages below re-read 2026-11-02; no 2026-27 foreign resident, WHM or Medicare levy low-income figures yet.
const CHECKED_ON = "2026-11-02"
const src = (label: string, url: string): Source => ({ label, url, checkedOn: CHECKED_ON })
const ATO = "https://www.ato.gov.au"

const RESIDENT = src(
  "ATO tax rates - Australian residents (updated 13 August 2026)",
  `${ATO}/tax-rates-and-codes/tax-rates-australian-residents`,
)
// The foreign resident and working holiday maker pages list years only up to 2025-26.
const FOREIGN = src(
  "ATO tax rates - foreign residents (updated 1 June 2026, to 2025-26)",
  `${ATO}/tax-rates-and-codes/tax-rates-foreign-residents`,
)
const WHM = src(
  "ATO tax rates - working holiday makers (updated 1 June 2026, to 2025-26)",
  `${ATO}/tax-rates-and-codes/tax-rates-working-holiday-makers`,
)
const LITO = src(
  "ATO low income tax offset (updated 8 June 2026)",
  `${ATO}/individuals-and-families/income-deductions-offsets-and-records/tax-offsets/low-income-tax-offset`,
)
// Page lists 2025-26 thresholds only; 2026-27 not published as of 2026-11-02 (page last updated 30 June 2026).
const MEDICARE = src(
  "ATO Medicare levy reduction for low-income earners (updated 30 June 2026, 2025-26 values)",
  `${ATO}/individuals-and-families/medicare-and-private-health-insurance/medicare-levy/medicare-levy-reduction/medicare-levy-reduction-for-low-income-earners`,
)
const HELP = src(
  "ATO study and training loan minimum repayment income and rates (updated 13 October 2026)",
  `${ATO}/tax-rates-and-codes/study-and-training-loan-minimum-repayment-income-and-rates`,
)
const SG = src(
  "ATO super guarantee (updated 17 April 2026)",
  `${ATO}/tax-rates-and-codes/key-superannuation-rates-and-thresholds/super-guarantee`,
)
const CONTRIBUTIONS_CAPS = src(
  "ATO contributions caps (updated 11 September 2026)",
  `${ATO}/tax-rates-and-codes/key-superannuation-rates-and-thresholds/contributions-caps`,
)
const REGISTERING_FOR_GST = src(
  "ATO registering for GST (updated 14 September 2026)",
  `${ATO}/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/registering-for-gst`,
)
const STANDARD_DEDUCTION = src(
  "ATO standard deduction for work-related expenses (updated 14 September 2026); Treasury Laws Amendment (Tax Reform No. 1) Act 2026, Schedule 4 (assented 26 June 2026)",
  `${ATO}/individuals-and-families/income-deductions-offsets-and-records/deductions-you-can-claim/work-related-deductions/standard-deduction-for-work-related-expenses`,
)

const b = (from: number, baseDollars: number, rateBp: number): Bracket => ({
  from,
  baseDollars,
  rateBp,
})
const band = (
  from: number,
  baseDollars: number,
  rateBp: number,
  ofWholeIncome = false,
): StudyLoanBand => ({ from, baseDollars, rateBp, ofWholeIncome })

// ATO 2025-26 foreign resident and working holiday maker tables (the latest year on those pages).
const FOREIGN_2025_26 = [b(0, 0, 3000), b(135000, 40500, 3700), b(190000, 60850, 4500)]
const WHM_2025_26 = [
  b(0, 0, 1500),
  b(45000, 6750, 3000),
  b(135000, 33750, 3700),
  b(190000, 54100, 4500),
]
const LITO_TABLE = {
  fullDollars: 700,
  firstTaperFrom: 37500,
  firstTaperBp: 500,
  secondFrom: 45000,
  secondBaseDollars: 325,
  secondTaperBp: 150,
  cutOut: 66667,
  source: LITO,
}
// ATO publishes the single-person thresholds for 2025-26 only; the shade-in rate is the
// Schedule 1 withholding formula's 10 cents per dollar over the lower threshold.
const MEDICARE_TABLE = {
  rateBp: 200,
  singleLower: 28011,
  singleUpper: 35013,
  shadeInBp: 1000,
  source: MEDICARE,
}
const whmTreatment = { medicare: "none", lito: false, treatmentStatus: "provisional" } as const

export const INCOME_TAX_YEARS: readonly IncomeTaxYear[] = Object.freeze([
  {
    financialYear: "2025-26",
    resident: {
      brackets: [
        b(0, 0, 0),
        b(18200, 0, 1600),
        b(45000, 4288, 3000),
        b(135000, 31288, 3700),
        b(190000, 51638, 4500),
      ],
      status: "published",
      source: RESIDENT,
    },
    foreignResident: { brackets: FOREIGN_2025_26, status: "published", source: FOREIGN },
    workingHoliday: { brackets: WHM_2025_26, status: "published", source: WHM, ...whmTreatment },
    lito: LITO_TABLE,
    medicare: { ...MEDICARE_TABLE, status: "published" },
    studyLoan: {
      bands: [
        band(0, 0, 0),
        band(67000, 0, 1500),
        band(125000, 8700, 1700),
        band(179285, 0, 1000, true),
      ],
      source: HELP,
    },
    // Table 24 gives A$62,500 per quarter; the annual figure is an approximation (spec section 2.1).
    superGuarantee: { rateBp: 1200, maxContributionBaseAnnual: 250000, source: SG },
    concessionalCap: { dollars: 30000, source: CONTRIBUTIONS_CAPS },
    standardDeduction: null,
  },
  {
    financialYear: "2026-27",
    resident: {
      brackets: [
        b(0, 0, 0),
        b(18200, 0, 1500),
        b(45000, 4020, 3000),
        b(135000, 31020, 3700),
        b(190000, 51370, 4500),
      ],
      status: "published",
      source: RESIDENT,
    },
    // Not on the ATO pages yet: the 2025-26 tables are carried forward and flagged provisional.
    foreignResident: { brackets: FOREIGN_2025_26, status: "provisional", source: FOREIGN },
    workingHoliday: { brackets: WHM_2025_26, status: "provisional", source: WHM, ...whmTreatment },
    lito: LITO_TABLE,
    medicare: { ...MEDICARE_TABLE, status: "provisional" },
    studyLoan: {
      bands: [
        band(0, 0, 0),
        band(69528, 0, 1500),
        band(129717, 9028, 1700),
        band(186050, 0, 1000, true),
      ],
      source: HELP,
    },
    superGuarantee: { rateBp: 1200, maxContributionBaseAnnual: 270830, source: SG },
    concessionalCap: { dollars: 32500, source: CONTRIBUTIONS_CAPS },
    standardDeduction: {
      maxDollars: 1000,
      residentsOnly: true,
      status: "published",
      source: STANDARD_DEDUCTION,
    },
  },
])

/** Rules for an income year; throws for a year not in the table (never guesses). */
export function incomeTaxYear(financialYear: string): IncomeTaxYear {
  const found = INCOME_TAX_YEARS.find((y) => y.financialYear === financialYear)
  if (!found) throw new RangeError(`No ATO income tax rules for income year "${financialYear}"`)
  return found
}

export type PayPeriod = "year" | "month" | "fortnight" | "week" | "day" | "hour"
export type Residency = "resident" | "foreign" | "working-holiday"
export type ShownPeriod = "year" | "month" | "fortnight" | "week"

export interface TakeHomeInput {
  financialYear: string
  amount: Money
  period: PayPeriod
  /** Used when `period` is `hour`; at most one decimal place. */
  hoursPerWeek: number
  /** Used when `period` is `day`. */
  daysPerWeek: number
  /** The amount already includes employer super. */
  includesSuper: boolean
  residency: Residency
  hasStudyLoan: boolean
  /** Annual income from another job (second job), default 0. */
  otherAnnualIncome: Money
  applyStandardDeduction: boolean
  /** Off for people the ATO exempts (medical, foreign resident, not entitled to Medicare benefits). */
  applyMedicareLevy: boolean
}

export interface AnnualBreakdown {
  /** Income from this job, without super. */
  gross: Money
  employerSuper: Money
  standardDeduction: Money
  /** Whole dollars. */
  taxableIncome: Money
  /** Before offsets. */
  incomeTax: Money
  lito: Money
  medicareLevy: Money
  studyLoanRepayment: Money
  takeHome: Money
  averageRateBp: number
  /** Next dollar of income: tax less LITO plus Medicare levy; HELP not included. */
  marginalRateBp: number
  /** Names of the rules used that the ATO has not finished publishing. */
  provisional: string[]
}

export interface PeriodBreakdown {
  gross: Money
  employerSuper: Money
  /** Tax after LITO. The balancing item, so the parts add up to `gross` to the cent. */
  incomeTaxAfterLito: Money
  medicareLevy: Money
  studyLoanRepayment: Money
  takeHome: Money
}

export interface TakeHomeResult {
  annual: AnnualBreakdown
  perPeriod: Record<ShownPeriod, PeriodBreakdown>
}

const PERIODS_PER_YEAR: Record<ShownPeriod, number> = {
  year: 1,
  month: 12,
  fortnight: 26,
  week: 52,
}
const MAX_AMOUNT_CENTS = 1_000_000_000

const aud = (cents: number) => money(cents, "AUD")

/** Integer division of non-negative numbers, half-up. */
function divRound(numerator: number, denominator: number): number {
  return Math.floor((numerator * 2 + denominator) / (denominator * 2))
}

/** `dollars` x `rateBp` as cents, half-up. */
const percentCents = (dollars: number, rateBp: number) => divRound(dollars * rateBp, 100)

/** Last row whose `from` is strictly below `income` (the ATO tables say "over $x"). */
function bandFor<T extends Bracket>(rows: readonly T[], income: number): T {
  let found = rows[0] as T
  for (const row of rows) if (income > row.from) found = row
  return found
}

function bracketTaxCents(brackets: readonly Bracket[], taxable: number): number {
  const row = bandFor(brackets, taxable)
  return row.baseDollars * 100 + percentCents(taxable - row.from, row.rateBp)
}

function litoCents(rule: IncomeTaxYear["lito"], taxable: number): number {
  if (taxable > rule.cutOut) return 0
  if (taxable <= rule.firstTaperFrom) return rule.fullDollars * 100
  const [baseDollars, from, taperBp] =
    taxable <= rule.secondFrom
      ? [rule.fullDollars, rule.firstTaperFrom, rule.firstTaperBp]
      : [rule.secondBaseDollars, rule.secondFrom, rule.secondTaperBp]
  // cents x 10,000 so the half-cent in 1.5% steps rounds once, at the end
  const scaled = baseDollars * 1_000_000 - (taxable - from) * taperBp * 100
  return scaled <= 0 ? 0 : divRound(scaled, 10_000)
}

function medicareCents(rule: IncomeTaxYear["medicare"], taxable: number): number {
  if (taxable <= rule.singleLower) return 0
  return Math.min(
    percentCents(taxable - rule.singleLower, rule.shadeInBp),
    percentCents(taxable, rule.rateBp),
  )
}

function studyLoanCents(rule: IncomeTaxYear["studyLoan"], income: number): number {
  const row = bandFor(rule.bands, income)
  if (row.ofWholeIncome) return percentCents(income, row.rateBp)
  return row.baseDollars * 100 + percentCents(income - row.from, row.rateBp)
}

/** A caller that omits the field (JSON, older code) keeps the levy; only an explicit false drops it. */
const applyLevy = (input: Pick<TakeHomeInput, "applyMedicareLevy">) =>
  input.applyMedicareLevy !== false

interface Parts {
  taxableIncome: number
  standardDeduction: number
  incomeTax: number
  lito: number
  medicare: number
  help: number
}

type TaxSettings = Pick<
  TakeHomeInput,
  "applyStandardDeduction" | "residency" | "applyMedicareLevy" | "hasStudyLoan"
>

function annualParts(
  year: IncomeTaxYear,
  input: TaxSettings,
  incomeCents: number,
): { parts: Parts; netTax: (taxable: number) => number } {
  const sd = year.standardDeduction
  const standardDeduction =
    input.applyStandardDeduction && input.residency === "resident" && sd
      ? Math.min(sd.maxDollars * 100, incomeCents)
      : 0
  const taxableIncome = Math.floor((incomeCents - standardDeduction) / 100)
  const rules =
    input.residency === "resident"
      ? year.resident
      : input.residency === "foreign"
        ? year.foreignResident
        : year.workingHoliday
  const hasLito =
    input.residency === "resident" ||
    (input.residency === "working-holiday" && year.workingHoliday.lito)
  const hasMedicare =
    applyLevy(input) &&
    (input.residency === "resident" ||
      (input.residency === "working-holiday" && year.workingHoliday.medicare === "resident-rules"))
  const incomeTax = bracketTaxCents(rules.brackets, taxableIncome)
  const lito = hasLito ? Math.min(litoCents(year.lito, taxableIncome), incomeTax) : 0
  const medicare = hasMedicare ? medicareCents(year.medicare, taxableIncome) : 0
  const help = input.hasStudyLoan ? studyLoanCents(year.studyLoan, taxableIncome) : 0
  const netTax = (taxable: number) => {
    const tax = bracketTaxCents(rules.brackets, taxable)
    const offset = hasLito ? Math.min(litoCents(year.lito, taxable), tax) : 0
    return tax - offset + (hasMedicare ? medicareCents(year.medicare, taxable) : 0)
  }
  return { parts: { taxableIncome, standardDeduction, incomeTax, lito, medicare, help }, netTax }
}

function annualise(input: TakeHomeInput): number {
  const { amount, period } = input
  if (period === "year") return amount.amount
  if (period === "hour") {
    const tenths = input.hoursPerWeek * 10
    if (
      !(input.hoursPerWeek >= 1 && input.hoursPerWeek <= 100) ||
      !Number.isInteger(Math.round(tenths * 1e6) / 1e6)
    ) {
      throw new RangeError("hoursPerWeek must be between 1 and 100 with at most one decimal place")
    }
    return multiplyByRatio(amount, Math.round(tenths) * 52, 10).amount
  }
  if (period === "day") {
    if (!Number.isInteger(input.daysPerWeek) || input.daysPerWeek < 1 || input.daysPerWeek > 7) {
      throw new RangeError("daysPerWeek must be a whole number from 1 to 7")
    }
    return amount.amount * input.daysPerWeek * 52
  }
  return amount.amount * PERIODS_PER_YEAR[period]
}

/** Splits an annual total into salary and employer super (spec section 4, step 2). */
function splitSuper(
  year: IncomeTaxYear,
  input: TakeHomeInput,
  annualCents: number,
): { salary: number; employerSuper: number } {
  const { rateBp, maxContributionBaseAnnual } = year.superGuarantee
  const maxBaseCents = maxContributionBaseAnnual * 100
  if (!input.includesSuper) {
    const base = Math.min(annualCents, maxBaseCents)
    return { salary: annualCents, employerSuper: multiplyByRatio(aud(base), rateBp, 10_000).amount }
  }
  let salary = multiplyByRatio(aud(annualCents), 10_000, 10_000 + rateBp).amount
  if (salary > maxBaseCents) {
    salary = annualCents - multiplyByRatio(aud(maxBaseCents), rateBp, 10_000).amount
  }
  return { salary, employerSuper: annualCents - salary }
}

function validate(input: TakeHomeInput): void {
  if (input.amount.currency !== "AUD" || input.otherAnnualIncome.currency !== "AUD") {
    throw new RangeError("Amounts must be in AUD")
  }
  for (const cents of [input.amount.amount, input.otherAnnualIncome.amount]) {
    if (cents < 0 || cents > MAX_AMOUNT_CENTS) {
      throw new RangeError("Amount must be between A$0 and A$10,000,000")
    }
  }
}

const pct = (cents: number, ofCents: number) =>
  ofCents === 0 ? 0 : divRound(cents * 10_000, ofCents)

/** Names of the rules used that the ATO has not finished publishing. */
function provisionalRules(
  year: IncomeTaxYear,
  input: Pick<TakeHomeInput, "residency" | "applyMedicareLevy">,
  usesStandardDeduction: boolean,
): string[] {
  const provisional: string[] = []
  if (input.residency === "foreign" && year.foreignResident.status !== "published")
    provisional.push("foreign-resident-brackets")
  if (input.residency === "working-holiday") {
    if (year.workingHoliday.status !== "published") provisional.push("working-holiday-brackets")
    if (year.workingHoliday.treatmentStatus !== "published")
      provisional.push("working-holiday-medicare-lito")
  }
  if (applyLevy(input) && input.residency === "resident" && year.medicare.status !== "published")
    provisional.push("medicare-levy-thresholds")
  if (usesStandardDeduction && year.standardDeduction?.status !== "published")
    provisional.push("standard-deduction")
  return provisional
}

/** Annual take-home pay estimate for one job, in integer cents. Pure; throws RangeError on bad input. */
export function takeHomePay(input: TakeHomeInput): TakeHomeResult {
  const year = incomeTaxYear(input.financialYear)
  validate(input)
  const { salary, employerSuper } = splitSuper(year, input, annualise(input))
  const other = input.otherAnnualIncome.amount

  const combined = annualParts(year, input, salary + other)
  let { incomeTax, lito, medicare, help } = combined.parts
  if (other > 0) {
    const alone = annualParts(year, input, other).parts
    // Net tax (after LITO) of this job = combined minus the other job alone; HELP's 9,028 step can go negative.
    const net = Math.max(0, incomeTax - lito - (alone.incomeTax - alone.lito))
    lito = Math.max(0, lito - alone.lito)
    incomeTax = net + lito
    medicare = Math.max(0, medicare - alone.medicare)
    help = Math.max(0, help - alone.help)
  }

  const taxAfterLito = incomeTax - lito
  const takeHome = salary - taxAfterLito - medicare - help
  const { taxableIncome } = combined.parts
  const marginalRateBp = (combined.netTax(taxableIncome + 1) - combined.netTax(taxableIncome)) * 100

  const provisional = provisionalRules(year, input, combined.parts.standardDeduction > 0)

  const annual: AnnualBreakdown = {
    gross: aud(salary),
    employerSuper: aud(employerSuper),
    standardDeduction: aud(combined.parts.standardDeduction),
    taxableIncome: aud(taxableIncome * 100),
    incomeTax: aud(incomeTax),
    lito: aud(lito),
    medicareLevy: aud(medicare),
    studyLoanRepayment: aud(help),
    takeHome: aud(takeHome),
    averageRateBp: pct(taxAfterLito + medicare + help, salary),
    marginalRateBp,
    provisional,
  }

  const perPeriod = {} as Record<ShownPeriod, PeriodBreakdown>
  for (const [name, n] of Object.entries(PERIODS_PER_YEAR) as [ShownPeriod, number][]) {
    const gross = divRound(salary, n)
    const net = divRound(takeHome, n)
    const med = divRound(medicare, n)
    const hl = divRound(help, n)
    perPeriod[name] = {
      gross: aud(gross),
      employerSuper: aud(divRound(employerSuper, n)),
      incomeTaxAfterLito: aud(gross - net - med - hl),
      medicareLevy: aud(med),
      studyLoanRepayment: aud(hl),
      takeHome: aud(net),
    }
  }
  return { annual, perPeriod }
}

/** GST registration threshold (ATO registering for GST, read 2026-11-02): GST turnover of A$75,000 or more. */
export const GST_REGISTRATION = {
  thresholdDollars: 75_000,
  source: REGISTERING_FOR_GST,
} as const

/** Sole trader (ABN) income for the whole year (T916). Amounts are annual and before GST. */
export interface SoleTraderInput {
  financialYear: string
  businessIncome: Money
  /** Business expenses the user can deduct, for the year. */
  businessExpenses: Money
  /** Salary or wages from an employer, for the year; 0 if none. */
  wages: Money
  residency: Residency
  hasStudyLoan: boolean
  applyMedicareLevy: boolean
}

export interface SoleTraderResult {
  /** Business income less expenses, never below zero. */
  profit: Money
  /** Expenses exceeded income; the loss is not set against wages (non-commercial loss rules are not modelled). */
  loss: boolean
  /** Wages plus profit, whole dollars. No standard deduction: it covers wages only. */
  taxableIncome: Money
  /** Extra tax caused by the business: the tax on wages plus profit less the tax on wages alone. */
  business: {
    /** Income tax after LITO. */
    incomeTaxAfterLito: Money
    medicareLevy: Money
    studyLoanRepayment: Money
    total: Money
  }
  /** Profit less the business tax. */
  takeHome: Money
  setAside: {
    perMonth: Money
    perQuarter: Money
    /** Business tax as a share of business income / of profit, in basis points. */
    shareOfIncomeBp: number
    shareOfProfitBp: number
  }
  /** Next dollar of income: tax less LITO plus Medicare levy; HELP not included. */
  marginalRateBp: number
  /** A sole trader has no employer paying super; the ATO figure for voluntary before-tax contributions. */
  employerSuper: Money
  concessionalCap: Money
  /** Business income at or over the GST threshold (the ATO also requires registration for taxi and ride-sourcing at any turnover). */
  gstRegistrationRequired: boolean
  provisional: string[]
}

/** Tax a sole trader sets aside on business profit, in integer cents. Pure; throws RangeError on bad input. */
export function soleTraderTax(input: SoleTraderInput): SoleTraderResult {
  const year = incomeTaxYear(input.financialYear)
  for (const m of [input.businessIncome, input.businessExpenses, input.wages]) {
    if (m.currency !== "AUD") throw new RangeError("Amounts must be in AUD")
    if (m.amount < 0 || m.amount > MAX_AMOUNT_CENTS) {
      throw new RangeError("Amount must be between A$0 and A$10,000,000")
    }
  }
  const settings: TaxSettings = {
    applyStandardDeduction: false,
    residency: input.residency,
    applyMedicareLevy: input.applyMedicareLevy,
    hasStudyLoan: input.hasStudyLoan,
  }
  const income = input.businessIncome.amount
  const profit = Math.max(0, income - input.businessExpenses.amount)
  const wages = input.wages.amount

  const combined = annualParts(year, settings, wages + profit)
  const alone = annualParts(year, settings, wages).parts
  const tax = Math.max(
    0,
    combined.parts.incomeTax - combined.parts.lito - (alone.incomeTax - alone.lito),
  )
  const medicare = Math.max(0, combined.parts.medicare - alone.medicare)
  const help = Math.max(0, combined.parts.help - alone.help)
  const total = tax + medicare + help
  const taxable = combined.parts.taxableIncome
  const rate = (combined.netTax(taxable + 1) - combined.netTax(taxable)) * 100

  return {
    profit: aud(profit),
    loss: input.businessExpenses.amount > income,
    taxableIncome: aud(taxable * 100),
    business: {
      incomeTaxAfterLito: aud(tax),
      medicareLevy: aud(medicare),
      studyLoanRepayment: aud(help),
      total: aud(total),
    },
    takeHome: aud(profit - total),
    setAside: {
      perMonth: aud(divRound(total, 12)),
      perQuarter: aud(divRound(total, 4)),
      shareOfIncomeBp: pct(total, income),
      shareOfProfitBp: pct(total, profit),
    },
    marginalRateBp: rate,
    employerSuper: aud(0),
    concessionalCap: aud(year.concessionalCap.dollars * 100),
    gstRegistrationRequired: income >= GST_REGISTRATION.thresholdDollars * 100,
    provisional: provisionalRules(year, settings, false),
  }
}
